import React, {lazy, useCallback, useContext, useEffect, useMemo, useRef} from 'react';
import {router} from "@/router";
import {RouterProvider} from "react-router-dom";
import {QueryCache, QueryClient} from "@tanstack/react-query";
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {PresenceType, useIdleTimer} from "react-idle-timer";
import {useAuth0} from "@auth0/auth0-react";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {useFeatureToggles} from "@/hooks/useFeatureToggles";
import SessionTimeoutWarning from "@/components/SessionTimeoutWarning";
import useHandleLogout from "@/hooks/useHandleLogout";
import useWalkMe from "@/hooks/useWalkMe";
import {log} from "@/helpers/logger";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      retry: 0
    },
  },
  queryCache: new QueryCache({
    onError: (e) => {
      // Log errors happening in the code between axios and tanstack, e.g. parsing the response
      log('error', 'Query error:', e);
    }
  })
}
);

const ReactQueryDevtoolsProduction = lazy(() =>
  import('@tanstack/react-query-devtools/build/modern/production.js').then(
    (d) => ({
      default: d.ReactQueryDevtools,
    }),
  ),
);

function App() {
  const timeout = useMemo(() => 1000 * 60 * (Number(process.env.REACT_APP_INACTIVITY_TIMEOUT_MINUTE) || 30), []);
  const promptBeforeIdle = useMemo(() => 1000 * 60 * (Number(process.env.REACT_APP_INACTIVITY_TIMEOUT_WARNING_MINUTE) || 10), []);

  const { getAccessTokenSilently } = useAuth0();
  const { webSocketApi } = useContext(CarecoApiContext);
  const { featureToggles, refetch } = useFeatureToggles();
  const [showDevtools, setShowDevtools] = React.useState(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = React.useState<boolean>(false);
  const handleLogout = useHandleLogout();
  useWalkMe();

  const hasExpired = useRef(false);

  const handleSessionExpired = useCallback(() => {
    if (hasExpired.current) return;
    hasExpired.current = true;

    setShowTimeoutWarning(false);
    handleLogout();
  }, [handleLogout]);

  const onPresenceChange = (presence: PresenceType) => {
    const isIdle = presence.type === 'idle';
    const isActive = presence.type === 'active' && !presence.prompted;
    const isPrompted = presence.type === 'active' && presence.prompted;

    if (isIdle) {
      handleSessionExpired();
    } else if (isActive) {
      setShowTimeoutWarning(false);
    } else if (isPrompted) {
      setShowTimeoutWarning(true);
    }
  };

  const { getRemainingTime, activate } = useIdleTimer({
    onPresenceChange,
    timeout,
    promptBeforeIdle,
    throttle: 500
  });

  const handleStillHere = useCallback(() => {
    getAccessTokenSilently({cacheMode: "off"})
      .then(() => {
        activate();
        refetch();
      })
      .catch(() => {
        log('error', 'Refreshing access token blocked. ' +
          'Check that all cookies are allowed in browser.');
      });
  }, [getAccessTokenSilently, activate, refetch]);

  useEffect(() => {
    window.toggleDevtools = () => setShowDevtools((old) => !old);
  }, []);

  useEffect(() => {
    if (!webSocketApi?.isReady() && featureToggles) {
      webSocketApi?.activate();
    }
  }, [webSocketApi, featureToggles]);

  return (
    <>
      {
        showTimeoutWarning &&
        <SessionTimeoutWarning
          handleStillHere={handleStillHere}
          getRemainingTime={getRemainingTime}
          onExpire={handleSessionExpired}
        />
      }
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
      <ReactQueryDevtools initialIsOpen={false} />
      {showDevtools && ['local', 'dev', 'qa'].includes(process.env.REACT_APP_ENV || 'prod') && (
        <React.Suspense fallback={null}>
          <ReactQueryDevtoolsProduction />
        </React.Suspense>
      )}
    </>
  );
}

export default App;
